'use client';
import React, { useState, useMemo } from 'react';
import AppLayout from '@/components/AppLayout';
import AppImage from '@/components/ui/AppImage';
import PriceBadge from '@/components/ui/PriceBadge';
import { PRODUCTS, CATEGORIES } from '@/data/marketData';
import { Search, SlidersHorizontal, Heart, X, ChevronDown } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';

type SortOption = 'price-asc' | 'price-desc' | 'change-desc' | 'change-asc' | 'popular';

export default function ProductCategoryBrowsePage() {
  const [lang, setLang] = useState<'en' | 'ta'>('en');
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('popular');
  const [watchedIds, setWatchedIds] = useState<Set<string>>(
    new Set(PRODUCTS.filter(p => p.isWatched).map(p => p.id))
  );
  const [showFilters, setShowFilters] = useState(false);
  const [trendFilter, setTrendFilter] = useState<'all' | 'drop' | 'rise' | 'stable'>('all');

  const toggleWatch = (productId: string, productName: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setWatchedIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
        toast(`${productName} removed from watchlist`, { icon: '🗑️' });
      } else {
        next.add(productId);
        toast(`${productName} added to watchlist`, { icon: '💚' });
      }
      return next;
    });
  };

  const filtered = useMemo(() => {
    let result = PRODUCTS;
    if (activeCategory !== 'all') result = result.filter(p => p.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.nameTa.includes(q) ||
        p.varieties.some(v => v.name.toLowerCase().includes(q))
      );
    }
    if (trendFilter !== 'all') result = result.filter(p => p.trend === trendFilter);
    switch (sort) {
      case 'price-asc': return [...result].sort((a, b) => a.currentPrice - b.currentPrice);
      case 'price-desc': return [...result].sort((a, b) => b.currentPrice - a.currentPrice);
      case 'change-desc': return [...result].sort((a, b) => b.priceChangePct - a.priceChangePct);
      case 'change-asc': return [...result].sort((a, b) => a.priceChangePct - b.priceChangePct);
      case 'popular': return [...result].sort((a, b) => b.watchlistCount - a.watchlistCount);
      default: return result;
    }
  }, [activeCategory, search, sort, trendFilter]);

  return (
    <AppLayout activeRoute="/product-category-browse" lang={lang} onLangToggle={() => setLang(l => l === 'en' ? 'ta' : 'en')}>
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 xl:px-10 2xl:px-16 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-1">
            {lang === 'en' ? 'Browse Products' : 'பொருட்களை உலாவு'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {lang === 'en' ? `${PRODUCTS.length} products · Updated 06:30 AM today` : `${PRODUCTS.length} பொருட்கள் · இன்று 06:30 AM புதுப்பிக்கப்பட்டது`}
          </p>
        </div>

        {/* Search + Filter Bar */}
        <div className="flex items-center gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={lang === 'en' ? 'Search vegetables, fruits, spices...' : 'காய்கறி, பழம், மசாலா தேடு...'}
              className="input-base pl-10 pr-4"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X size={14} />
              </button>
            )}
          </div>

          <div className="relative">
            <select
              value={sort}
              onChange={e => setSort(e.target.value as SortOption)}
              className="input-base appearance-none pr-8 cursor-pointer bg-card border border-border text-sm"
              style={{ width: '160px' }}
            >
              <option value="popular">Most Tracked</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="change-asc">Biggest Drops</option>
              <option value="change-desc">Biggest Rises</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-3 rounded-2xl border text-sm font-medium transition-all ${showFilters ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary'}`}
          >
            <SlidersHorizontal size={16} />
            <span className="hidden sm:inline">Filter</span>
          </button>
        </div>

        {/* Filter Chips */}
        {showFilters && (
          <div className="flex items-center gap-2 mb-5 p-4 bg-card rounded-2xl border border-border">
            <span className="text-xs font-semibold text-muted-foreground mr-2">Trend:</span>
            {[
              { id: 'all', label: 'All' },
              { id: 'drop', label: '📉 Drops' },
              { id: 'rise', label: '📈 Rises' },
              { id: 'stable', label: '➡️ Stable' },
            ].map(f => (
              <button
                key={`filter-${f.id}`}
                onClick={() => setTrendFilter(f.id as typeof trendFilter)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${trendFilter === f.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-secondary'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 mb-6">
          {CATEGORIES.map(cat => (
            <button
              key={`cat-tab-${cat.id}`}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold transition-all duration-200 border ${
                activeCategory === cat.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card text-muted-foreground border-border hover:border-primary hover:text-primary'
              }`}
            >
              <span>{cat.icon}</span>
              <span>{lang === 'en' ? cat.name : cat.nameTa}</span>
              <span className={`text-xs rounded-full px-1.5 py-0.5 ${activeCategory === cat.id ? 'bg-primary-foreground/20' : 'bg-muted'}`}>
                {cat.id === 'all' ? PRODUCTS.length : PRODUCTS.filter(p => p.category === cat.id).length}
              </span>
            </button>
          ))}
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            {filtered.length} {lang === 'en' ? 'products found' : 'பொருட்கள் கண்டுபிடிக்கப்பட்டன'}
          </p>
          {(search || trendFilter !== 'all') && (
            <button
              onClick={() => { setSearch(''); setTrendFilter('all'); }}
              className="text-xs text-primary font-semibold hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Product Grid */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="text-6xl mb-4">🔍</span>
            <h3 className="text-lg font-bold text-foreground mb-2">
              {lang === 'en' ? 'No products found' : 'பொருட்கள் கிடைக்கவில்லை'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {lang === 'en' ? 'Try a different search term or category' : 'வேறு தேடல் அல்லது வகையை முயற்சிக்கவும்'}
            </p>
            <button
              onClick={() => { setSearch(''); setActiveCategory('all'); setTrendFilter('all'); }}
              className="btn-primary"
            >
              {lang === 'en' ? 'Show All Products' : 'அனைத்து பொருட்களும்'}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
            {filtered.map(product => {
              const isWatched = watchedIds.has(product.id);
              return (
                <Link key={`browse-${product.id}`} href={`/product-detail?id=${product.id}`} className="block group">
                  <div className="card-base card-hover overflow-hidden h-full flex flex-col">
                    {/* Image */}
                    <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                      <AppImage
                        src={product.image}
                        alt={product.imageAlt}
                        fill
                        sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
                        className="object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <button
                        onClick={(e) => toggleWatch(product.id, product.name, e)}
                        className={`watchlist-heart absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full glass-card ${isWatched ? 'text-red-alert' : 'text-muted-foreground'}`}
                        aria-label={isWatched ? 'Remove from watchlist' : 'Add to watchlist'}
                      >
                        <Heart size={15} fill={isWatched ? 'currentColor' : 'none'} />
                      </button>
                      {product.isSeasonal && (
                        <span className="absolute bottom-2 left-2 text-xs font-semibold bg-accent text-accent-foreground rounded-full px-2 py-0.5">
                          🌿
                        </span>
                      )}
                    </div>

                    {/* Details */}
                    <div className="p-3 flex-1 flex flex-col">
                      <p className="font-semibold text-sm text-foreground truncate">
                        {lang === 'en' ?product.name : product.nameTa}
                      </p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {lang === 'en' ? product.nameTa : product.name}
                      </p>
                      <div className="flex items-center justify-between mt-auto pt-2">
                        <div>
                          <span className="price-value text-base">₹{product.currentPrice}</span>
                          <span className="text-xs text-muted-foreground">/{product.unit}</span>
                        </div>
                        <PriceBadge trend={product.trend} change={product.priceChange} changePct={product.priceChangePct} />
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-muted-foreground font-tabular line-through">
                          ₹{product.previousPrice}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {product.varieties.length}v
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}